import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { MonitorResponse, MonitorRun } from "./types";
import { useQueueResource } from "./useQueueResource";
import { label } from "./format";

type History = MonitorResponse & {
  items: MonitorRun[];
  next_cursor: string | null;
  current_execution_epoch: number;
};

export function QueueExecutionHistory({
  workload,
  currentEpoch,
  viewedEpoch,
  onSelect,
}: {
  workload: string;
  currentEpoch: number;
  viewedEpoch: number;
  onSelect: (epoch: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const resource = useQueueResource<History>(
    open
      ? `/pipeline/runs/${encodeURIComponent(workload)}/epochs${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`
      : null,
    "detail",
  );
  return (
    <section className="space-y-2 text-sm" aria-label="Execution history">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          Execution {viewedEpoch}
          {viewedEpoch === currentEpoch
            ? " · current"
            : " · historical evidence"}
        </span>
        {viewedEpoch !== currentEpoch && (
          <Button size="sm" variant="outline" onClick={() => onSelect(null)}>
            Current execution
          </Button>
        )}
        {currentEpoch > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setOpen((value) => !value)}
          >
            Earlier executions
          </Button>
        )}
      </div>
      {open && (
        <div className="rounded border p-3">
          {resource.loading && !resource.data && (
            <p role="status">Loading execution history…</p>
          )}
          {resource.error && (
            <p role="status">
              History unavailable.{" "}
              <Button variant="link" onClick={resource.reload}>
                Retry
              </Button>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {resource.data?.items.map((run) => (
              <Button
                key={run.execution_epoch}
                variant="outline"
                size="sm"
                onClick={() => onSelect(run.execution_epoch)}
              >
                Execution {run.execution_epoch} · {label(run.lifecycle)}
              </Button>
            ))}
          </div>
          {resource.data?.next_cursor && (
            <Button
              variant="link"
              onClick={() => setCursor(resource.data!.next_cursor)}
            >
              Older executions
            </Button>
          )}
          {cursor && (
            <Button variant="link" onClick={() => setCursor(null)}>
              Newest history
            </Button>
          )}
          {!resource.loading &&
            !resource.error &&
            !resource.data?.items.length && (
              <p>No earlier execution evidence retained.</p>
            )}
        </div>
      )}
    </section>
  );
}
